/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Classes, ControlGroup, TagInput } from '@blueprintjs/core';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { useFilteredProperties } from 'utilities/srsUtils';
import { PropertyType as SrsPropertyType } from 'srs-middleware';
import { Variables } from 'styles/variables';
import { sendUsageAction } from 'utilities/usageStats';

const { updateFilter, updateQueryString } = WorkspaceActionCreator;

interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	buttonText?: string;
	large?: boolean;
	propertiesToSearch?: string[];
	// searchField: ??? // what is this supposed to be?
}

/**
 * Search is a "filter provider".  It is responsible for:
 * 1) updating state with a human readable Search-based filter
 * 2) responding to changes in the Search filter and updating state with the proper query syntax
 *
 * @returns {*}
 * @constructor
 */
export const Search: React.FunctionComponent<Props> = ({
	buttonText = 'Search',
	large = false,
	placeholder = 'Enter search terms...',
	propertiesToSearch = [],
	...props
}) => {
	const {
		state: {
			explorer: {
				filters: {
					search: searchTerms = {
						terms: [],
						fields: [],
					},
				},
				selectedDefinitionName,
			},
		},
		dispatch,
	} = useContext(WorkspaceContext);
	const { terms } = searchTerms;

	const [text, setText] = useState<string>('');

	const allSearchable = useTextSearchableProperties(selectedDefinitionName, propertiesToSearch);

	const updateSearchFilter = (newTerms: string[]) => {
		setText('');

		if (newTerms.length === 0) {
			resetTextFilters(dispatch, allSearchable);
		} else {
			dispatch(
				updateFilter('search', {
					terms: newTerms,
					fields: allSearchable,
				})
			);
		}
	};

	const handleUsageStat = useCallback((terms: string[]) => {
		sendUsageAction('SearchTermEntered', terms[0]);
	}, []);

	const clearSearch = () => {
		setText('');
		if (terms.length > 0) updateSearchFilter([]);
	};

	const addTextToTerms = () => {
		if (text === '') return;
		updateSearchFilter([...terms, text]);
	};

	useEffect(() => {
		if (searchTerms.terms.length === 0) {
			return;
		}
		// input is {terms: ['term1', 'term2'], fields: ['Title', 'Abstract']}

		// $or for each field per term
		const perField = searchTerms.terms.map((term) => {
			return searchTerms.fields.map((field) => {
				return { [field]: { $c: term } };
			});
		});

		// this works for all cases where we still have a term left, but fails
		// when we have cleared the last term.
		dispatch(
			updateQueryString('search', {
				$and: perField.map((fieldArray) => {
					return { $or: fieldArray };
				}),
			})
		);
	}, [searchTerms, dispatch]);

	return (
		<div css={searchWrapperStyle({ large })} {...props}>
			<ControlGroup cy-test="search" fill>
				<TagInput
					onChange={updateSearchFilter}
					inputValue={text}
					onInputChange={(e) => setText((e.target as HTMLInputElement).value)}
					values={terms}
					onAdd={handleUsageStat}
					placeholder={placeholder}
					rightElement={
						<Button cy-test="clear-search" onClick={clearSearch} icon={'cross'} minimal disabled={terms.length === 0 && text.length === 0} />
					}
					tagProps={{
						minimal: true,
					}}
					fill
					large={large}
					leftIcon={'search'}
				/>
				<Button
					cy-test="search-records"
					text={buttonText}
					intent={'primary'}
					type="submit"
					onClick={addTextToTerms}
					disabled={text.length === 0}
					large={large}
					className={Classes.FIXED}
					css={submitButtonStyle}
					// rightIcon={'search'}
				/>
			</ControlGroup>
		</div>
	);
};

// handler for resetting filters in Workspace state
export function resetTextFilters(dispatch, allSearchable: string[]) {
	dispatch(
		updateFilter('search', {
			terms: [],
			fields: allSearchable,
		})
	);

	// if we've deleted the last term, force a queryString update to null
	dispatch(updateQueryString('search', null));
}

// selects text searchable properties based on selectedDefinitionName
export function useTextSearchableProperties(selectedDefinitionName, propertiesToSearch) {
	const allSearchable = useFilteredProperties(
		selectedDefinitionName,
		[],
		[SrsPropertyType.Text, SrsPropertyType.String],
		propertiesToSearch
	).map((p) => p.name);
	return allSearchable;
}

const searchWrapperStyle = ({ large }) => css`
	// this will let the <TagInput/> expand outside its container when its focused and tags overflow
	height: ${large ? Variables.PT_INPUT_HEIGHT_LARGE : Variables.PT_INPUT_HEIGHT};
	display: flex;
	z-index: 11;
	flex-direction: row;

	&:focus-within {
		flex-direction: column;
	}

	.${Classes.TAG_INPUT_VALUES} {
		overflow-y: auto; // scroll vertically when overflowing (not focused)
		width: 1px; // this tricks flexbox into not indefinitely expanding horizontally
	}

	.${Classes.LARGE} .${Classes.TAG_REMOVE} {
		padding: 0 5px 0 0; // blueprint bug? fixed in updated version?
	}

	${Bp3ControlGroupFix}
`;

const submitButtonStyle = css`
	align-self: flex-start;
`;

const Bp3ControlGroupFix = css`
	// this is just fixing blueprint TagInput as part of a ControlGroup
	.${Classes.CONTROL_GROUP} {
		.${Classes.TAG_INPUT} {
			&.${Classes.ACTIVE} {
				border-radius: ${Variables.PT_BORDER_RADIUS};
				z-index: 14;
			}

			.${Classes.BUTTON} {
				// this is also a blueprint oversight?
				border-radius: ${Variables.PT_BORDER_RADIUS};
			}
		}
	}
`;
